/* eslint-disable @typescript-eslint/no-explicit-any */
// Logs: bitácora técnica de lo que el Hub intercambia con Magaya y las demás integraciones (iTraceabilities).
// Pantalla del Control Panel: colores de Innobo Hub (no los del cliente). Quien no es rol 19 solo ve su empresa.
// La lista viene liviana (sin payloads); el detalle completo se pide por id al abrir la fila.
// Las fechas llegan en hora del servidor (UTC) como "YYYY/MM/DD hh:mm:ss AM"; aquí se muestran en la hora local.
// Todas las opciones salen de los registros del rango. Client, Operation y Task aceptan varios valores (OR).
// Stop corta la espera de una consulta larga (el servidor la termina igual).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import PageTitle from '../../components/Common/MetaTagsHelper/metaTagsHelper';
import { getNameClient } from '../../helpers/nameClient';
import usePageTitle from '../../common/hooks/usePageTitle';
import { getExchange, countExchange, getExchangeDetail, getExchangeOptions, esAdminHub, detenerConsultas } from '../../helpers/exchange_helper';
import {
  Page,
  Card,
  Field,
  TextInput,
  DateRange,
  Segmented,
  Stat,
  Badge,
  Button,
  DataTable,
  Drawer,
  MetaGrid,
  Alert,
  EmptyState,
} from '../../components/ihub';
import { ExportMenu, traerTodo } from '../../components/ihub/export';
import { MultiSelectField } from '../../components/ihub/multiselect';

const F = 'YYYY-MM-DD';
const VACIO = () => ({
  from: moment().format(F),
  to: moment().format(F),
  clientId: [] as string[],
  operation: [] as string[],
  taskId: [] as string[],
  level: '',
  search: '',
});
const OPERACIONES: any = {
  CR: 'Cargo Release',
  WH: 'Warehouse Receipt',
  WR: 'Warehouse Receipt',
  SO: 'Sales Order',
  PO: 'Purchase Order',
  WI: 'Item',
  SH: 'Shipment',
  PK: 'Pickup Order',
  IV: 'Item Definition',
  IN: 'Invoice',
  QT: 'Quotation',
  BK: 'Booking',
  ET: 'Entity',
};
const operacion = (o: any) => (o ? OPERACIONES[o] || o : '-');
// El backend manda createdAt como "YYYY/MM/DD hh:mm:ss AM" en hora del servidor (UTC): se pasa a la hora local.
const fecha = (s: any) => {
  if (!s) return null;
  const m = moment.utc(s, 'YYYY/MM/DD hh:mm:ss A', true);
  return m.isValid() ? m.local() : moment(s);
};
const dia = (s: any) => (fecha(s) ? (fecha(s) as any).format('MMM D, YYYY') : '');
const hora = (s: any) => (fecha(s) ? (fecha(s) as any).format('HH:mm:ss') : '');
const completa = (s: any) => (fecha(s) ? (fecha(s) as any).format('ddd, MMM D YYYY · HH:mm:ss') : '');
const corto = (s: any, n = 140) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};
const nivel = (l: any) => {
  const v = String(l || '').toUpperCase();
  if (v === 'ERROR') return { tone: 'danger', label: 'Error' };
  if (v === 'INFO') return { tone: 'info', label: 'Info' };
  if (v === 'DEBUG') return { tone: 'neutral', label: 'Debug' };
  return { tone: 'neutral', label: v || '-' };
};
// JSON bonito si se puede, texto tal cual si no.
const legible = (v: any) => {
  if (v === null || v === undefined) return '';
  const t = String(v).trim();
  if (!t) return '';
  try {
    return JSON.stringify(JSON.parse(t), null, 2);
  } catch (e) {
    return t;
  }
};

export const Traceabilities = (props: any) => {
  const admin = esAdminHub();
  const titulo = String(usePageTitle(props.location.pathname) || '').replace(/-/g, ' ') || 'Logs';
  const [filters, setFilters] = useState<any>(VACIO);
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<any>({ key: 'createdAt', dir: 'DESC' });
  const [data, setData] = useState<{ listData: any[]; CountItems: number }>({ listData: [], CountItems: 0 });
  const [stats, setStats] = useState<any>(null);
  const [opciones, setOpciones] = useState<{ operations: string[]; tasks: any[] }>({ operations: [], tasks: [] });
  const [clientes, setClientes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fila, setFila] = useState<any>(null);
  const [detalle, setDetalle] = useState<any>(null);
  const [copiado, setCopiado] = useState('');
  const [recarga, setRecarga] = useState(0);
  const abierta = useRef<any>(null);

  const order = `{'${sort.key}' : '${sort.dir}'}`;
  const poner = (patch: any) => {
    setFilters((f: any) => ({ ...f, ...patch }));
    setPage(1);
  };
  const limpiar = () => {
    setBusqueda('');
    setFilters(VACIO());
    setPage(1);
  };
  const sucio = busqueda !== '' || JSON.stringify({ ...filters, search: '' }) !== JSON.stringify(VACIO());
  const nombreCliente = (id: any) => {
    const c = clientes.find((x: any) => String(x.id) === String(id));
    return c ? c.name : `Client ${id}`;
  };

  // Búsqueda mientras escribes, con una pausa corta para no disparar una consulta por tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f: any) => {
        if (f.search === busqueda) return f;
        setPage(1);
        return { ...f, search: busqueda };
      });
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Opciones de los selects: solo lo que existe en el rango (y cliente) elegido.
  // Se piden otra vez con Refresh y cuando cambia el total, para que no queden vacías con datos en la tabla.
  const opcionesKey = JSON.stringify([filters.from, filters.to, filters.clientId]);
  useEffect(() => {
    let vivo = true;
    const [from, to, clientId] = JSON.parse(opcionesKey);
    getExchangeOptions({ from, to, clientId })
      .then((o: any) => {
        if (!vivo) return;
        setOpciones({ operations: o.operations || [], tasks: o.tasks || [] });
        if (!clientId.length) setClientes(o.clients || []);
      })
      .catch(() => null);
    // Con clientes elegidos, la lista de clientes del rango se pide aparte (sin acotar) para poder sumar otro.
    if (admin && clientId.length) {
      getExchangeOptions({ from, to, clientId: [] })
        .then((o: any) => vivo && setClientes(o.clients || []))
        .catch(() => null);
    }
    return () => {
      vivo = false;
    };
  }, [opcionesKey, recarga, data.CountItems, admin]);

  // Lista
  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError('');
    getExchange({ pagination: { sizePerPage: pageSize, page }, filters, order })
      .then((d: any) => vivo && setData(d))
      .catch((e: any) => vivo && !e?.stopped && setError(e?.message || 'Error'))
      .finally(() => vivo && setLoading(false));
    return () => {
      vivo = false;
    };
  }, [filters, page, pageSize, order, recarga]);

  // Métricas del rango (sin el filtro de nivel, para que las tarjetas sirvan de filtro rápido)
  const baseKey = JSON.stringify([filters.from, filters.to, filters.clientId, filters.operation, filters.taskId, filters.search]);
  useEffect(() => {
    let vivo = true;
    const [from, to, clientId, operation, taskId, search] = JSON.parse(baseKey);
    const base = { from, to, clientId, operation, taskId, search, level: '' };
    Promise.all([countExchange(base), countExchange({ ...base, level: 'ERROR' }), countExchange({ ...base, level: 'OK' })])
      .then(([total, errors, ok]) => vivo && setStats({ total, errors, ok }))
      .catch(() => vivo && setStats({}));
    return () => {
      vivo = false;
    };
  }, [baseKey, recarga]);

  const abrir = async (row: any) => {
    abierta.current = row.id;
    setFila(row);
    setDetalle(null);
    let d: any = {};
    try {
      d = (await getExchangeDetail(row.id)) || {};
    } catch (e) {
      d = {};
    }
    if (abierta.current === row.id) setDetalle(d);
  };
  const cerrar = useCallback(() => {
    abierta.current = null;
    setFila(null);
    setDetalle(null);
  }, []);
  const detener = () => {
    detenerConsultas();
    setLoading(false);
    setStats((x: any) => x || {});
  };
  const copiar = (k: string, v: string) => {
    if (navigator.clipboard) navigator.clipboard.writeText(v).catch(() => null);
    setCopiado(k);
    setTimeout(() => setCopiado(''), 1500);
  };

  const columns = [
    {
      key: 'createdAt',
      label: 'Date',
      sortable: true,
      width: 140,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main">{dia(r.createdAt)}</span>
          <span className="ihub-cell-sub">{hora(r.createdAt)}</span>
        </>
      ),
    },
    {
      key: 'Operation',
      label: 'Operation',
      sortable: true,
      width: 170,
      render: (r: any) => <Badge tone="info">{operacion(r.Operation)}</Badge>,
    },
    {
      key: 'Number',
      label: 'Number',
      sortable: true,
      width: 170,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main">{r.Number || '-'}</span>
          {r.WRNUMBER ? <span className="ihub-cell-sub">WR {r.WRNUMBER}</span> : null}
        </>
      ),
    },
    {
      key: 'taskId',
      label: 'Task',
      sortable: true,
      width: 230,
      render: (r: any) => (
        <>
          <span className="ihub-cell-main ihub-truncate" style={{ maxWidth: 230 }}>
            {r.iTask?.descripcion || (r.taskId && r.taskId !== '0' ? `Task ${r.taskId}` : 'No task')}
          </span>
          {r.taskId && r.taskId !== '0' ? <span className="ihub-cell-sub">#{r.taskId}</span> : null}
        </>
      ),
    },
    {
      key: 'text1',
      label: 'Detail',
      render: (r: any) => (
        <>
          <span className="ihub-cell-main ihub-truncate">{corto(r.text1) || '-'}</span>
          {r.text2 ? <span className="ihub-cell-sub ihub-truncate">{corto(r.text2)}</span> : null}
        </>
      ),
    },
    ...(admin
      ? [
          {
            key: 'ClientId',
            label: 'Client',
            sortable: true,
            width: 180,
            render: (r: any) => (
              <>
                <span className="ihub-cell-main ihub-truncate" style={{ maxWidth: 180 }}>
                  {r.ClientId ? nombreCliente(r.ClientId) : '-'}
                </span>
                {r.ClientId ? <span className="ihub-cell-sub">#{r.ClientId}</span> : null}
              </>
            ),
          },
        ]
      : []),
    {
      key: 'logLevel',
      label: 'Level',
      sortable: true,
      width: 110,
      render: (r: any) => {
        const n = nivel(r.logLevel);
        return (
          <Badge tone={n.tone} dot>
            {n.label}
          </Badge>
        );
      },
    },
  ];

  // Exportar: todo lo que cumple los filtros (lista liviana: sin los payloads grandes de Detail 3 y 4).
  const exportCols = [
    { label: 'Date', width: 20, value: (r: any) => (fecha(r.createdAt) ? (fecha(r.createdAt) as any).format('YYYY-MM-DD HH:mm:ss') : '') },
    { label: 'Operation', width: 18, value: (r: any) => operacion(r.Operation) },
    { label: 'Number', width: 20, value: (r: any) => r.Number },
    { label: 'WR Number', width: 14, value: (r: any) => r.WRNUMBER },
    { label: 'Task', width: 26, value: (r: any) => r.iTask?.descripcion || (r.taskId && r.taskId !== '0' ? r.taskId : '') },
    { label: 'Detail', width: 50, value: (r: any) => r.text1 },
    { label: 'Detail 2', width: 40, value: (r: any) => r.text2 },
    ...(admin ? [{ label: 'Client', width: 20, value: (r: any) => (r.ClientId ? `${nombreCliente(r.ClientId)} #${r.ClientId}` : '') }] : []),
    { label: 'Level', width: 10, value: (r: any) => nivel(r.logLevel).label },
  ];
  const exportarTodo = () => traerTodo((p: number, size: number) => getExchange({ pagination: { sizePerPage: size, page: p }, filters, order }));

  const cargandoStats = stats === null;
  const s = stats || {};
  const d = detalle || {};
  const textos = [
    { k: 'text2', label: 'Detail 2', v: legible(d.text2) },
    { k: 'text5', label: 'Additional detail', v: legible(d.text5) },
  ].filter((x) => x.v);
  const datos = [
    { k: 'text3', label: 'Detail 3', v: legible(d.text3) },
    { k: 'text4', label: 'Detail 4', v: legible(d.text4) },
  ].filter((x) => x.v);

  return (
    <>
      <Page
        eyebrow="Control Panel"
        title={titulo}
        subtitle="Technical log of everything the Hub exchanges with Magaya and the other integrations: what ran, when, and what came back."
        actions={
          <>
            <ExportMenu fileName={`logs_${filters.from}_${filters.to}`} title={titulo} columns={exportCols} fetchAll={exportarTodo} />
            {loading || cargandoStats ? (
              <Button variant="ghost" icon="bx-stop-circle" onClick={detener}>
                Stop
              </Button>
            ) : null}
            <Button variant="secondary" icon="bx-refresh" onClick={() => setRecarga((n) => n + 1)}>
              Refresh
            </Button>
          </>
        }
      >
        <PageTitle>
          <title>
            {titulo} | {getNameClient()}
          </title>
        </PageTitle>

        <div className="ihub-stats">
          <Stat
            label="Total"
            icon="bx-transfer"
            tone="info"
            value={s.total}
            loading={cargandoStats}
            active={!filters.level}
            onClick={() => poner({ level: '' })}
          />
          <Stat
            label="Errors"
            icon="bx-error-circle"
            tone="danger"
            value={s.errors}
            loading={cargandoStats}
            active={filters.level === 'ERROR'}
            onClick={() => poner({ level: 'ERROR' })}
          />
          <Stat
            label="Without errors"
            icon="bx-check-circle"
            tone="success"
            value={s.ok}
            loading={cargandoStats}
            active={filters.level === 'OK'}
            onClick={() => poner({ level: 'OK' })}
          />
        </div>

        <Card>
          <div className="ihub-filters ihub-filters--wide">
            <Field label="Search" className="ihub-filters__search">
              <TextInput
                icon="bx-search"
                placeholder="Number, WR number, task id or detail"
                value={busqueda}
                onChange={(e: any) => setBusqueda(e.target.value)}
              />
            </Field>
            <Field label="Date range">
              <DateRange from={filters.from} to={filters.to} onChange={(from: string, to: string) => poner({ from, to })} />
            </Field>
            {admin ? (
              <Field label="Client">
                <MultiSelectField
                  placeholder="All clients"
                  options={clientes.map((c: any) => ({ value: String(c.id), label: `${c.name} · #${c.id}` }))}
                  value={filters.clientId}
                  onChange={(v: string[]) => poner({ clientId: v })}
                />
              </Field>
            ) : null}
            <Field label="Operation">
              <MultiSelectField
                placeholder="All operations"
                options={opciones.operations.map((o: string) => ({ value: o, label: `${operacion(o)} (${o})` }))}
                value={filters.operation}
                onChange={(v: string[]) => poner({ operation: v })}
              />
            </Field>
            <Field label="Task">
              <MultiSelectField
                placeholder="All tasks"
                options={opciones.tasks.map((t: any) => ({ value: String(t.id), label: `${t.name} · #${t.id}` }))}
                value={filters.taskId}
                onChange={(v: string[]) => poner({ taskId: v })}
              />
            </Field>
            <Field label="Level">
              <Segmented
                options={[
                  { value: '', label: 'All' },
                  { value: 'ERROR', label: 'Errors' },
                  { value: 'OK', label: 'OK' },
                ]}
                value={filters.level}
                onChange={(v: any) => poner({ level: v })}
              />
            </Field>
          </div>
          {sucio ? (
            <div className="ihub-filters__foot">
              <Button variant="ghost" size="sm" icon="bx-reset" onClick={limpiar}>
                Clear filters
              </Button>
            </div>
          ) : null}
        </Card>

        {error ? (
          <Alert tone="danger" title="Couldn't load the logs">
            {error}
          </Alert>
        ) : null}

        <Card flush>
          <DataTable
            columns={columns}
            rows={data.listData}
            total={data.CountItems}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n: number) => {
              setPageSize(n);
              setPage(1);
            }}
            sort={sort}
            onSort={(nuevo: any) => {
              setSort(nuevo);
              setPage(1);
            }}
            onRowClick={abrir}
            loading={loading}
            empty={
              <EmptyState
                icon="bx-transfer"
                title="Nothing in this range"
                text={sucio ? 'Try a wider date range or clear the filters.' : 'Logs show up here as soon as the Hub processes something.'}
              />
            }
          />
        </Card>
      </Page>

      <Drawer
        open={!!fila}
        onClose={cerrar}
        eyebrow={fila ? `${operacion(fila.Operation)} · #${fila.id}` : ''}
        title={fila ? corto(fila.text1, 200) || operacion(fila.Operation) : ''}
        badges={
          fila ? (
            <>
              <Badge tone={nivel(fila.logLevel).tone} dot>
                {nivel(fila.logLevel).label}
              </Badge>
              {fila.Number ? <Badge>{fila.Number}</Badge> : null}
            </>
          ) : null
        }
      >
        {fila ? (
          <>
            <MetaGrid
              items={[
                { label: 'Operation', value: `${operacion(fila.Operation)}${fila.Operation ? ` (${fila.Operation})` : ''}` },
                { label: 'Number', value: fila.Number || '-' },
                { label: 'WR Number', value: fila.WRNUMBER || '-' },
                {
                  label: 'Task',
                  value: (
                    <>
                      {fila.iTask?.descripcion || '-'}
                      {fila.taskId && fila.taskId !== '0' ? <span className="ihub-cell-sub">#{fila.taskId}</span> : null}
                    </>
                  ),
                },
                { label: 'Date', value: completa(fila.createdAt) },
                { label: 'User', value: fila.user || '-' },
                ...(admin
                  ? [
                      {
                        label: 'Client',
                        value: fila.ClientId ? (
                          <>
                            {nombreCliente(fila.ClientId)} <span className="ihub-muted">#{fila.ClientId}</span>
                          </>
                        ) : (
                          '-'
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            {fila.text1 && String(fila.text1).length > 200 ? (
              <Alert tone={nivel(fila.logLevel).tone === 'danger' ? 'danger' : 'info'} title="Detail">
                {fila.text1}
              </Alert>
            ) : null}
            {detalle === null ? (
              <Card>
                <div className="ihub-email__loading">
                  <div className="ihub-skel" style={{ width: '60%' }} />
                  <div className="ihub-skel" style={{ width: '90%' }} />
                  <div className="ihub-skel" style={{ width: '75%' }} />
                </div>
              </Card>
            ) : (
              <div className="ihub-blocks">
                {textos.map((x) => (
                  <Card flush key={x.k}>
                    <div className="ihub-block__head">
                      <span>{x.label}</span>
                    </div>
                    <div className="ihub-block__text">{x.v}</div>
                  </Card>
                ))}
                {datos.map((x) => (
                  <Card flush key={x.k}>
                    <div className="ihub-block__head">
                      <span>{x.label}</span>
                      <Button variant="ghost" size="sm" icon={copiado === x.k ? 'bx-check' : 'bx-copy'} onClick={() => copiar(x.k, x.v)}>
                        {copiado === x.k ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <pre className="ihub-pre">{x.v}</pre>
                  </Card>
                ))}
                {!textos.length && !datos.length ? (
                  <Card>
                    <EmptyState icon="bx-file-blank" title="No extra data" text="This log only has the summary above." />
                  </Card>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </Drawer>
    </>
  );
};

export default Traceabilities;
